import {forwardRef, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentEditor from 'crqlar-react-common/dist/components/ContentEditor.component';
import CommonConfirmDialog from 'crqlar-react-common/dist/components/dialog/CommonConfirmDialog';
import {ContentSource} from 'crqlar-react-common/dist/models/content/content.model';
import {useTranslation} from 'react-i18next';
import {builderMergeTags} from '../../../config/builderMergeTags';
import {builderOverwritesHtml} from '../../../config/emailModes';
import type {AvailableVariable} from '../../../config/nodeVariables';
import EmailContentPreview, {EMAIL_CONTENT_HEIGHT, EMAIL_CONTENT_HEIGHT_EXPANDED} from './EmailContentPreview';

export interface BuilderEmailContent {
    content: string;
    unlayerSourceData?: string;
}

export interface EmailBuilderHandle {
    flush: () => Promise<BuilderEmailContent | undefined>;
}

interface Props extends BuilderEmailContent {
    onChange: (content: BuilderEmailContent) => void;
    variables: AvailableVariable[];
    onWriteHtml?: () => void;
    expanded?: boolean;
    /** Grow to the rest of a dialog of fixed height even when it is not enlarged (Send team email). */
    fill?: boolean;
    readOnly?: boolean;
}

const EmailBuilderField = forwardRef<EmailBuilderHandle, Props>(({
    content, unlayerSourceData, onChange, variables, onWriteHtml, expanded, fill, readOnly
}, ref) => {
    const grow = expanded || fill;
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [confirmEmpty, setConfirmEmpty] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    const editor = useRef<ContentEditor>(null);
    const acceptAutosaves = useRef(true);
    const openBuilder = () => { acceptAutosaves.current = true; setOpen(true); };
    const mergeTags = useMemo(() => builderMergeTags(variables, {
        name: t('Hotel'),
        fields: [
            {label: t('Hotel name'), key: 'hotelName'},
            {label: t('Hotel city'), key: 'hotelCity'},
            {label: t('Hotel phone'), key: 'hotelPhone'},
            {label: t('Hotel email'), key: 'hotelEmail'}
        ]
    }), [variables, t]);
    const savedContent = (saved: ContentSource): BuilderEmailContent => ({
        content: saved.generatedHtmlCode || '', unlayerSourceData: saved.content
    });
    // Final exports own the saved snapshot; an older autosave callback can finish during the close transition.
    const flush = async () => {
        if (!open) return undefined;
        acceptAutosaves.current = false;
        setExporting(true);
        setExportError(false);
        try {
            if (!editor.current) throw new Error('Builder is not ready');
            const saved = savedContent(await editor.current.exportContent());
            onChange(saved);
            return saved;
        } catch (error) {
            acceptAutosaves.current = true;
            setExportError(true);
            throw error;
        } finally {
            setExporting(false);
        }
    };
    useImperativeHandle(ref, () => ({flush}));
    const close = async () => {
        if (exporting) return;
        try { await flush(); setOpen(false); } catch { /* Keep the unsaved builder open. */ }
    };

    return <>
        <Stack gap={1} sx={grow ? {flex: '1 0 auto'} : undefined}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                <Typography variant={'body1'} fontWeight={500} color={'text.secondary'}>{t('Email Content')}</Typography>
                <Stack direction={'row'} gap={1} alignItems={'center'} flexWrap={'wrap'}>
                    <Button variant={'contained'} disabled={readOnly} onClick={() => {
                        if (builderOverwritesHtml({content, unlayerSourceData})) setConfirmEmpty(true);
                        else openBuilder();
                    }}>{t('Open builder')}</Button>
                    {onWriteHtml && <Button color={'inherit'} disabled={readOnly} onClick={onWriteHtml}
                                            sx={{color: 'text.secondary'}}>{t('Write HTML instead')}</Button>}
                </Stack>
            </Stack>
            <EmailContentPreview content={content} height={expanded ? EMAIL_CONTENT_HEIGHT_EXPANDED : EMAIL_CONTENT_HEIGHT} fill={grow}/>
        </Stack>
        <CommonConfirmDialog open={confirmEmpty} onClose={() => setConfirmEmpty(false)}
                             onConfirm={() => {setConfirmEmpty(false); openBuilder();}}
                             title={t('Start this email in the builder?')}
                             message={t('The builder cannot open HTML written by hand. It starts empty and replaces this email once you open it.')}
                             confirmLabel={t('Start in the builder')}/>
        <Dialog open={open} onClose={() => void close()} maxWidth={'lg'} fullWidth
                PaperProps={{sx: {height: '90vh', maxHeight: '90vh'}}}>
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Typography variant={'h6'} fontWeight={600} noWrap>{t('Email Builder')}</Typography>
                    <Tooltip title={t('Close')}><span><IconButton disabled={exporting} onClick={() => void close()}
                        size={'small'} aria-label={t('Close')}><CloseIcon/></IconButton></span></Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: 0, display: 'flex', flexDirection: 'column'}}>
                {exportError && <Alert severity={'error'}>{t('The email could not be saved yet. Wait for the builder to load, then try again.')}</Alert>}
                <Box sx={{flexGrow: 1, minHeight: 0}}>
                    <ContentEditor ref={editor} content={{content: unlayerSourceData, generatedHtmlCode: content} as ContentSource}
                                   onSaved={saved => {if (saved && acceptAutosaves.current) onChange(savedContent(saved));}}
                                   displayMode={'email'} mergeTags={mergeTags} mergeTagsLabel={t('Variables')}/>
                </Box>
            </DialogContent>
            <DialogActions><Button disabled={exporting} onClick={() => void close()}>{t('Close')}</Button></DialogActions>
        </Dialog>
    </>;
});

export default EmailBuilderField;
